import { useCallback, useEffect, useRef, useState } from "react";
import { Search, SlidersHorizontal, PackageOpen } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta, typeLabel } from "@/lib/format";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OfferingCard } from "@/components/marketing/OfferingCard";
import { EmptyState } from "@/components/EmptyState";

const ALL = "__all__";
const PAGE_SIZE = 24;

export default function Marketplace() {
  const t = useT();
  usePageMeta(t("mkt.meta"), t("mkt.meta_desc"));

  const [filters, setFilters] = useState({ types: [], regions: [], tags: [] });
  const [q, setQ] = useState("");
  const [type, setType] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [theme, setTheme] = useState(ALL);

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/public/offerings/filters").then((r) => setFilters(r.data || {})).catch(() => {});
  }, []);

  const fetchPage = useCallback(async (pageNum, replace) => {
    const params = { page: pageNum, page_size: PAGE_SIZE };
    if (q.trim()) params.q = q.trim();
    if (type !== ALL) params.type = type;
    if (region !== ALL) params.region = region;
    if (theme !== ALL) params.theme = theme;
    const { data } = await api.get("/public/offerings", { params });
    setTotal(data.total || 0);
    setHasMore(!!data.has_more);
    setItems((prev) => (replace || !prev ? data.items || [] : [...prev, ...(data.items || [])]));
  }, [q, type, region, theme]);

  // Re-fetch (page 0) whenever filters change, debounced on the search text.
  useEffect(() => {
    setItems(null);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPage(0, true).catch(() => setItems([]));
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [fetchPage]);

  const loadMore = async () => {
    const next = page + 1;
    setLoadingMore(true);
    try {
      await fetchPage(next, false);
      setPage(next);
    } finally {
      setLoadingMore(false);
    }
  };

  const resetFilters = () => {
    setQ("");
    setType(ALL);
    setRegion(ALL);
    setTheme(ALL);
  };

  const activeFilters = [type !== ALL, region !== ALL, theme !== ALL, !!q.trim()].filter(Boolean).length;

  return (
    <div>
      <div className="border-b border-border bg-secondary/40">
        <div className="container-page py-12">
          <h1 className="font-display text-4xl text-foreground">{t("mkt.title")}</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">
            {t("mkt.subtitle")}
          </p>

          <div className="mt-7 flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("mkt.search_ph")}
                className="h-11 rounded-full bg-card pl-10"
                data-testid="marketplace-search"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-11 w-[150px] rounded-full bg-card" data-testid="filter-type">
                  <SelectValue placeholder={t("mkt.art")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("mkt.all_types")}</SelectItem>
                  {(filters.types || []).map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {typeLabel({ type: t.value })} ({t.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="h-11 w-[150px] rounded-full bg-card" data-testid="filter-region">
                  <SelectValue placeholder={t("mkt.region")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("mkt.all_regions")}</SelectItem>
                  {(filters.regions || []).map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.value} ({r.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger className="h-11 w-[150px] rounded-full bg-card" data-testid="filter-theme">
                  <SelectValue placeholder={t("mkt.theme")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("mkt.all_themes")}</SelectItem>
                  {(filters.tags || []).map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.value} ({t.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {activeFilters > 0 && (
            <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
              <SlidersHorizontal className="h-4 w-4" />
              <span>{t("mkt.filters_active", { n: activeFilters })}</span>
              <Button variant="ghost" size="sm" className="h-7 rounded-full" onClick={resetFilters} data-testid="clear-filters">
                {t("mkt.reset")}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="container-page py-10">
        {items !== null && (
          <p className="mb-6 text-sm text-muted-foreground" data-testid="result-count">
            {total === 1 ? t("mkt.count_one", { n: total }) : t("mkt.count_other", { n: total })}
          </p>
        )}

        {items === null ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={PackageOpen}
            title={t("mkt.empty_title")}
            description={t("mkt.empty_desc")}
            action={<Button variant="outline" className="rounded-full" onClick={resetFilters}>{t("mkt.reset_filters")}</Button>}
          />
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((o) => <OfferingCard key={o.id} offering={o} />)}
            </div>
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <Button variant="outline" className="rounded-full px-8" onClick={loadMore} disabled={loadingMore} data-testid="load-more">
                  {loadingMore ? t("mkt.loading") : t("mkt.load_more")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
